import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { X, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import { REGISTRAR_PAGO } from '../../graphql/cuentasCobro';

function formatMoneda(v) {
  if (v == null) return '$0';
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
}

function ModalRegistrarPago({ cuenta, onClose, onSuccess }) {
  const [monto, setMonto] = useState('');
  const [metodoPago, setMetodoPago] = useState('efectivo');
  const [referencia, setReferencia] = useState('');
  const [notas, setNotas] = useState('');

  const [registrarPago, { loading }] = useMutation(REGISTRAR_PAGO);

  const saldo = parseFloat(cuenta.saldo_pendiente || 0);
  const montoNum = parseFloat(monto) || 0;
  const esValido = montoNum > 0 && montoNum <= saldo && (metodoPago !== 'transferencia' || referencia.trim());

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!esValido) return;

    try {
      await registrarPago({
        variables: {
          input: {
            cuenta_cobro_id: cuenta.id,
            monto: montoNum,
            metodo_pago: metodoPago,
            referencia: referencia || null,
            notas: notas || null,
          }
        }
      });
      onSuccess();
    } catch (err) {
      toast.error(err.message || 'Error al registrar pago');
    }
  };

  const pagoCompleto = () => {
    setMonto(String(saldo));
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-cc modal-cc--sm" onClick={e => e.stopPropagation()}>
        <div className="modal-cc__header">
          <h2>Registrar Pago</h2>
          <button className="btn-cerrar" onClick={onClose}><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-cc__body">
            {/* Info de la cuenta */}
            <div className="pago-cuenta-info">
              <div><strong>{cuenta.nombre_soldado}</strong></div>
              <div className="pago-cuenta-info__codigo">{cuenta.codigo}</div>
              <div className="pago-saldo">
                Saldo pendiente: <strong>{formatMoneda(saldo)}</strong>
              </div>
            </div>

            <div className="form-group">
              <label>Monto del pago</label>
              <div className="monto-input-group">
                <span className="monto-prefix">$</span>
                <input
                  type="number"
                  value={monto}
                  onChange={e => setMonto(e.target.value)}
                  placeholder="0"
                  min="1"
                  max={saldo}
                  step="1"
                  required
                  autoFocus
                />
                <button type="button" className="btn-pago-completo" onClick={pagoCompleto}>
                  Pago completo
                </button>
              </div>
              {montoNum > saldo && (
                <p className="error-msg">El monto excede el saldo pendiente</p>
              )}
            </div>

            <div className="form-group">
              <label>Método de pago</label>
              <select value={metodoPago} onChange={e => setMetodoPago(e.target.value)}>
                <option value="efectivo">Efectivo</option>
                <option value="transferencia">Transferencia</option>
              </select>
            </div>

            {metodoPago === 'transferencia' && (
              <div className="form-group">
                <label>Número de referencia <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  type="text"
                  value={referencia}
                  onChange={e => setReferencia(e.target.value)}
                  placeholder="Número de transferencia"
                  required
                />
                {!referencia.trim() && (
                  <p className="error-msg">La referencia es obligatoria para transferencias</p>
                )}
              </div>
            )}

            <div className="form-group">
              <label>Notas (opcional)</label>
              <textarea
                value={notas}
                onChange={e => setNotas(e.target.value)}
                placeholder="Observaciones del pago..."
                rows={2}
              />
            </div>
          </div>

          <div className="modal-cc__footer">
            <button type="button" className="btn-modal" onClick={onClose}>Cancelar</button>
            <button
              type="submit"
              className="btn-modal btn-modal--primary"
              disabled={!esValido || loading}
            >
              {loading ? <Loader size={16} className="spinner" /> : `Registrar ${formatMoneda(montoNum)}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* Estilos del modal pago */
const style = document.createElement('style');
style.textContent = `
.pago-cuenta-info { padding: 0.75rem; background: var(--hotel-background); border-radius: 8px; margin-bottom: 1rem; }
.pago-cuenta-info strong { font-size: 0.95rem; }
.pago-cuenta-info__codigo { font-family: monospace; font-size: 0.8rem; color: var(--hotel-text-secondary); }
.pago-saldo { margin-top: 0.5rem; font-size: 0.9rem; }
.pago-saldo strong { color: #ef4444; font-size: 1.1rem; }
.monto-input-group { display: flex; align-items: center; gap: 0.5rem; }
.monto-prefix { font-size: 1.25rem; font-weight: 700; color: var(--hotel-text-secondary); }
.monto-input-group input { flex: 1; font-size: 1.25rem; font-weight: 700; padding: 0.5rem; }
.btn-pago-completo { padding: 0.4rem 0.75rem; border: 1px solid var(--hotel-primary); background: transparent; color: var(--hotel-primary); border-radius: 6px; font-size: 0.75rem; font-weight: 600; cursor: pointer; white-space: nowrap; }
.btn-pago-completo:hover { background: var(--hotel-primary); color: white; }
.error-msg { font-size: 0.8rem; color: #ef4444; margin: 0.25rem 0 0; }
`;
if (!document.getElementById('modal-pago-styles')) {
  style.id = 'modal-pago-styles';
  document.head.appendChild(style);
}

export default ModalRegistrarPago;
